import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import Layout from '../components/Layout';

type FormStatus = 'idle' | 'pending' | 'success' | 'error';

const Contact = () => {
  useEffect(() => {
    // Tawk.to Script
    const Tawk_API = (window as any).Tawk_API || {};
    const Tawk_LoadStart = new Date();
    (window as any).Tawk_API = Tawk_API;
    (window as any).Tawk_LoadStart = Tawk_LoadStart;

    const s1 = document.createElement('script');
    const s0 = document.getElementsByTagName('script')[0];
    s1.async = true;
    s1.src = 'https://embed.tawk.to/697c657f435d921c378eac00/1jg6uouho';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s0?.parentNode?.insertBefore(s1, s0);

    // Cleanup on unmount
    return () => {
      const tawkScript = document.querySelector('script[src*="tawk.to"]');
      if (tawkScript) {
        tawkScript.remove();
      }
      // Remove Tawk widget if it exists
      const tawkWidget = document.querySelector('[class*="tawk"]');
      if (tawkWidget) {
        tawkWidget.remove();
      }
    };
  }, []);

  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<{ type: FormStatus; message: string }>({
    type: 'idle',
    message: '',
  });

  const setField = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    console.log('Contact form update', { field: name, value });
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus({ type: 'pending', message: 'Sending your message...' });
    console.log('Contact submit triggered', formData);

    try {
      const response = await fetch('/api/send_message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get('content-type') ?? '';
      let payload: Record<string, unknown> | null = null;
      if (contentType.includes('application/json')) {
        payload = await response.json();
      } else {
        const text = await response.text();
        throw new Error(text || 'Server returned a non-JSON response');
      }

      console.log('Contact form response', { status: response.status, payload });

      if (!response.ok) {
        const serverError = typeof payload?.error === 'string' ? payload.error : 'Unable to send message';
        throw new Error(serverError);
      }

      console.log('Contact message saved', payload);

      setStatus({ type: 'success', message: 'Thanks! Pascaline received your message.' });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Failed to submit contact message', error);
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    }
  };

  return (
    <Layout>
      <section className="contact contact-page" aria-label="Contact page">
        <div className="contact__details">
          <p className="contact__eyebrow">Ready to chat</p>
          <h1>Reach Out</h1>
          <p>
           Have questions about Pascaline, partnerships, school deployments, or community sponsorships? Reach out.
          </p>
          <div className="contact__info">
            <a href="mailto:hello@pascalinekids.org">hello@pascalinekids.org</a>
            <a href="tel:+2349054821617">+234 905 482 1617</a>
            <span>No. 51, Road 15, NTA Road - Port Harcourt  · Remote-friendly</span>
          </div>
        </div>
        <form
          className="contact__form"
          aria-label="Contact form"
          onSubmit={handleSubmit}
        >
          <label className="sr-only" htmlFor="contact-page-name">
            Name
          </label>
          <input
            id="contact-page-name"
            name="name"
            placeholder="Your name"
            required
            value={formData.name}
            onChange={setField}
          />
          <label className="sr-only" htmlFor="contact-page-email">
            Email
          </label>
          <input
            id="contact-page-email"
            name="email"
            type="email"
            placeholder="Your email"
            required
            value={formData.email}
            onChange={setField}
          />
          <label className="sr-only" htmlFor="contact-page-message">
            Message
          </label>
          <textarea
            id="contact-page-message"
            name="message"
            rows={5}
            placeholder="Tell us what you are curious about"
            required
            value={formData.message}
            onChange={setField}
          />
          {status.message && (
            <p
              role="status"
              aria-live="polite"
              className={`contact__status contact__status--${status.type}`}
            >
              {status.message}
            </p>
          )}
          <button
            type="submit"
            className="primary"
            disabled={status.type === 'pending'}
          >
            Send message
          </button>
        </form>
      </section>
    </Layout>
  );
};

export default Contact;
